// src/lib/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { getBusinessByEmail } from './airtable';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading]   = useState(true);

  // Retry logic: Airtable has a slight indexing delay after record creation
  async function loadBusiness(email, retries = 3, delay = 500) {
    for (let i = 0; i < retries; i++) {
      const biz = await getBusinessByEmail(email);
      if (biz) {
        setBusiness(biz);
        return biz;
      }
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // exponential backoff: 500ms → 1s → 2s
      }
    }
    setBusiness(null);
    return null;
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user?.email) {
        loadBusiness(session.user.email).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user?.email) {
        loadBusiness(session.user.email);
      } else {
        setBusiness(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await loadBusiness(email);
    return data;
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setBusiness(null);
  }

  function refreshBusiness() {
    if (user?.email) return loadBusiness(user.email);
  }

  return (
    <AuthContext.Provider value={{ user, business, loading, signUp, signIn, signOut, refreshBusiness }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);